import { useState } from 'react';
import { Link } from 'react-router';
import { useLang, useT } from '../app/context';
import { myLink, useHost } from '../app/host';
import { errorText, fieldErrors, request } from '../lib/api';
import { Icon } from '../ui/Icon';
import { Button, Field, Input, Textarea } from '../ui/kit';
import { PublicFrame } from './Start';

/** my.buildiary.app: start a trial, go to (or find) your workspace; the platform team's console is at /console/. */
export function MyHomePage() {
  const t = useT();
  const host = useHost();
  const [name, setName] = useState('');
  const base = host.baseDomain ?? 'buildiary.app';
  const go = () => {
    const n = name.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\..*$/, '').replace(/[^a-z0-9-]/g, '');
    if (n) window.location.assign(`https://${n}.${base}`);
  };
  return (
    <PublicFrame kicker="my.buildiary.app" headline={t('my.title')} lead={t('si.promise')}>
      <div className="stack" style={{ gap: 6 }}>
        <h2>{t('my.go')}</h2>
        <p className="muted" style={{ fontSize: 15 }}>{t('my.lead')}</p>
      </div>
      <form className="form" onSubmit={(e) => { e.preventDefault(); go(); }}>
        <Field label={t('my.goHint')}>
          <div className="slug-field">
            <span className="sfx" style={{ borderInlineStart: 0, borderInlineEnd: '1px solid var(--line)' }}>https://</span>
            <input aria-label={t('my.goHint')} value={name} autoCapitalize="none" spellCheck={false} placeholder="alnoor" onChange={(e) => setName(e.target.value)} />
            <span className="sfx">.{base}</span>
          </div>
        </Field>
        <div className="row-wrap" style={{ gap: 10 }}>
          <Button type="submit" variant="primary" icon="arrowRight" disabled={!name.trim()} style={{ minHeight: 46 }}>{t('my.go')}</Button>
          <Link className="btn" to="/find" style={{ minHeight: 46 }}>{t('si.find')}</Link>
        </div>
      </form>
      <div className="divider">{t('si.or')}</div>
      <Link className="btn primary" to="/start" style={{ alignSelf: 'flex-start', minHeight: 46 }}>{t('si.start')}</Link>
      <p className="muted small" style={{ marginTop: 'auto' }}>
        <a href="/console/">{t('my.console')}</a>{host.websiteUrl ? <> · <a href={host.websiteUrl}>{t('nows.site')}</a></> : null}
      </p>
    </PublicFrame>
  );
}

/** Find my workspace: the workspace addresses are emailed to the person (the page never shows them). */
export function FindPage() {
  const t = useT();
  const { lang } = useLang();
  const host = useHost();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await request('POST /api/find-workspace', { body: { email: email.trim(), lang } });
      setDone(r.message);
    } catch (e) {
      setErr(fieldErrors(e).email ?? errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <PublicFrame kicker="Buildiary" headline={t('find.title')}>
      <div className="stack" style={{ gap: 6 }}>
        <h2>{t('find.title')}</h2>
        <p className="muted" style={{ fontSize: 15 }}>{t('find.lead')}</p>
      </div>
      {done ? (
        <div className="alert success" role="status"><Icon name="tick" size={16} />{done}</div>
      ) : (
        <form className="form" onSubmit={(e) => { e.preventDefault(); void send(); }} noValidate>
          <Field label={t('find.email')} error={err}><Input type="email" autoComplete="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} invalid={!!err} /></Field>
          <Button type="submit" variant="primary" loading={busy} disabled={!email.includes('@')} style={{ minHeight: 46, alignSelf: 'flex-start' }}>{t('find.send')}</Button>
        </form>
      )}
      <p className="muted small"><a href={myLink(host, '/start')}>{t('si.start')}</a></p>
    </PublicFrame>
  );
}

/** <name>.buildiary.app with no workspace behind it. */
export function NoWorkspacePage() {
  const t = useT();
  const host = useHost();
  return (
    <PublicFrame kicker={host.host} headline={t('nows.title')}>
      <div className="stack" style={{ gap: 6 }}>
        <h2>{t('nows.title')}</h2>
        <p className="muted" style={{ fontSize: 15 }}><span className="latin">{host.host}</span></p>
        <p style={{ fontSize: 15 }}>{t('nows.text')}</p>
      </div>
      <div className="row-wrap" style={{ gap: 10 }}>
        <a className="btn primary" href={myLink(host, '/find')} style={{ minHeight: 46 }}>{t('si.find')}</a>
        <a className="btn" href={myLink(host, '/start')} style={{ minHeight: 46 }}>{t('si.start')}</a>
        {host.websiteUrl && <a className="btn ghost" href={host.websiteUrl} style={{ minHeight: 46 }}>{t('nows.site')}</a>}
      </div>
    </PublicFrame>
  );
}

/** /feedback without signing in (1.1's feedback page). Signed-in people use “Send feedback” in the account menu. */
export function PublicFeedbackPage() {
  const t = useT();
  const { lang } = useLang();
  const [v, setV] = useState({ name: '', email: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      await request('POST /api/feedback/public', { body: { ...v, lang } });
      setDone(true);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <PublicFrame kicker="Buildiary" headline={t('top.feedback')}>
      <h2>{t('top.feedback')}</h2>
      {done ? (
        <div className="alert success" role="status"><Icon name="tick" size={16} />Thank you. Your message was sent.</div>
      ) : (
        <form className="form" onSubmit={(e) => { e.preventDefault(); void send(); }} noValidate>
          <Field label={t('st.name')}><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
          <Field label={t('st.email')}><Input type="email" dir="ltr" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
          <Field label="Your message"><Textarea rows={5} value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} /></Field>
          {err && <div className="alert danger" role="alert"><Icon name="warn" size={16} />{err}</div>}
          <Button type="submit" variant="primary" loading={busy} disabled={!v.name || !v.email || !v.text} style={{ alignSelf: 'flex-start' }}>{t('c.send')}</Button>
        </form>
      )}
    </PublicFrame>
  );
}
